import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useRef, useState, useEffect } from "react";
import Layout from "@/components/Layout";
import HeroBackground from "@/components/HeroBackground";
import ctaText from "@/assets/text1.webp";
import ctaTextEn from "@/assets/text1-en.webp";
import heroTitle from "@/assets/hero-title.png";
import heroTitleEn from "@/assets/hero-title-en.png";
import infographic from "@/assets/infographic-euronova.png";

import { useLanguage } from "@/contexts/LanguageContext";

const Index = () => {
  const { lang, t } = useLanguage();
  const ctaImgRef = useRef<HTMLImageElement>(null);
  const [ctaHeight, setCtaHeight] = useState(0);

  useEffect(() => {
    if (!ctaImgRef.current) return;
    // ResizeObserver delivers entries with already-computed sizes asynchronously,
    // so it never triggers a forced synchronous reflow.
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const h = entry.contentRect.height;
        setCtaHeight(prev => (Math.abs(prev - h) > 0.5 ? h : prev));
      }
    });
    ro.observe(ctaImgRef.current);
    return () => ro.disconnect();
  }, []);

  return (
    <Layout>
      {/* Hero */}
      <section className="relative min-h-screen flex flex-col overflow-hidden">
        <HeroBackground />
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-background/80 to-transparent pointer-events-none z-[5]" />
        <div className="relative z-[4] flex-1 flex flex-col py-20 sm:py-32 pointer-events-none [&_a]:pointer-events-auto [&_button]:pointer-events-auto">
          <div className="flex-1 flex items-center w-full">
            <div className="w-full animate-fade-up container mx-auto px-6 flex flex-col items-center sm:items-start">
                <div style={{ maxWidth: lang === "ro" ? '32.8rem' : '38rem' }} className="w-full text-left sm:ml-[-18px]">
                <img src={lang === "ro" ? heroTitle : heroTitleEn} alt={lang === "ro" ? "Printuri de mari dimensiuni" : "Large Format Prints"} className="w-full select-none block" fetchPriority="high" width={1920} height={1272} />
              </div>
              <div style={{ maxWidth: lang === "ro" ? '49.2rem' : '57rem' }} className="w-full sm:ml-[-16px]">
                <img
                    ref={ctaImgRef}
                    src={lang === "ro" ? ctaText : ctaTextEn}
                    alt={lang === "ro" ? "O imagine face cât o mie de cuvinte. Noi facem mii de imagini!" : "An image says more than a thousand words. We produce thousands of images!"}
                    className="w-full select-none"
                    width={1920}
                    height={381}
                    style={{ marginTop: ctaHeight > 0 ? `${ctaHeight * 0.5}px` : '4rem' }}
                  />
              </div>
              <div style={{ maxWidth: lang === "ro" ? '49.2rem' : '57rem' }} className="w-full">
                <div className="flex flex-wrap justify-between sm:justify-start gap-8 mt-6">
                  <Link
                    to="/servicii"
                    className="inline-flex items-center gap-2 bg-gradient-brand px-6 py-3 rounded-lg font-semibold text-sm text-primary-foreground hover:opacity-90 transition-opacity"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    {t.hero.seeServices} <ArrowRight size={16} />
                  </Link>
                  <Link
                    to="/contact"
                    onClick={() => window.scrollTo(0, 0)}
                    className="inline-flex items-center gap-2 bg-gradient-brand px-6 py-3 rounded-lg font-semibold text-sm text-primary-foreground hover:opacity-90 transition-opacity"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    {t.hero.requestQuote}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section className="pt-24 pb-16 bg-section-alt">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="font-display text-sm font-semibold uppercase tracking-[0.2em] text-primary mb-2">{t.about.subtitle}</p>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-8">
                {t.about.title} <span style={{ color: '#FF0000' }}>{t.about.titleHighlight}</span>
              </h2>
              <div className="space-y-5 text-muted-foreground leading-relaxed">
                <p>{t.about.p1}</p>
                <p>{t.about.p2_start}<strong className="text-foreground">{t.about.p2_bold}</strong>{t.about.p2_end}</p>
                <p>{t.about.p3_start}<strong className="text-foreground">{t.about.p3_bold}</strong>{t.about.p3_end}</p>
                <p>{t.about.p4_start}<strong className="text-foreground">{t.about.p4_bold}</strong>{t.about.p4_end}</p>
                <p>{t.about.p5}</p>
                <p className="font-display font-semibold text-foreground">{t.about.p6}</p>
              </div>
            </div>

            <div className="flex flex-col items-center lg:sticky lg:top-32">
              <div className="w-full rounded-xl overflow-hidden shadow-lg">
                <video src="/videos/about-video.MOV" muted loop playsInline preload="auto" className="w-full h-auto cursor-pointer"
                  onMouseEnter={(e) => e.currentTarget.play()}
                  onMouseLeave={(e) => { e.currentTarget.pause(); e.currentTarget.currentTime = 0; }}
                  onTouchStart={(e) => {
                    const video = e.currentTarget;
                    if (video.paused) { video.play(); } else { video.pause(); video.currentTime = 0; }
                  }} />
              </div>
              <p className="mt-4 text-sm text-muted-foreground text-center italic">{t.about.videoCaption1}</p>
              <div className="w-full rounded-xl overflow-hidden shadow-lg mt-8">
                <video src="/videos/about-video-2.mp4" muted loop playsInline preload="metadata" className="w-full h-auto cursor-pointer"
                  onMouseEnter={(e) => e.currentTarget.play().catch(() => {})}
                  onMouseLeave={(e) => { e.currentTarget.pause(); e.currentTarget.currentTime = 0; }}
                  onTouchStart={(e) => {
                    const video = e.currentTarget;
                    if (video.paused) { video.play().catch(() => {}); } else { video.pause(); video.currentTime = 0; }
                  }}
                  onError={(e) => e.currentTarget.removeAttribute('src')} />
              </div>
              <p className="mt-4 text-sm text-muted-foreground text-center italic">{t.about.videoCaption2}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12">
        <div className="container mx-auto px-6">
          <div className="text-center">
              <h2 className="font-display text-3xl md:text-4xl font-bold mb-2 text-foreground">
                {t.cta.title} <span className="text-gradient-brand">{t.cta.titleHighlight}</span>?
              </h2>
              <p className="text-muted-foreground max-w-lg mx-auto mb-6">{t.cta.description}</p>
              <Link
                to="/contact"
                onClick={() => window.scrollTo(0, 0)}
                className="inline-flex items-center gap-2 bg-gradient-brand px-8 py-4 rounded-lg font-semibold text-sm text-primary-foreground hover:opacity-90 transition-opacity"
                style={{ fontFamily: "'Montserrat', sans-serif" }}>
                <span>{t.cta.button}</span> <ArrowRight size={16} />
              </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
